import angular from 'angular';
import moment from 'moment';
import { Empresa, Estabelecimento } from '../../../../core/usuario/models/usuario-model';
import { IRotatividadeAgrupada, RotatividadeService } from '../rotatividade.service';
import { ListagemService } from '../../../../shared/services/listagem.service';

type IRotatividadeIndexFilter = {
    empresa?: string;
    estabelecimento?: string;
    lotacao?: { lotacao: string } | null;
    cargo?: { cargo: string } | null;
    tipo: string;
    agrupamento: 'nenhum' | 'estabelecimento' | 'lotacao' | 'cargo';
    exibirtotais: boolean;
};

export class RotatividadeIndexController {
    static $inject = [
        '$rootScope',
        '$state',
        '$stateParams',
        'NewToaster',
        'RotatividadeService',
        'ListagemService',
        'RelatoriosService',
    ];

    public carregando = false;
    public resultado: IRotatividadeAgrupada | null = null;
    public carregandoRelatorio: boolean;
    public modo_selecao: 'competencia' | 'range';
    public competencia: any;
    public range = {
        datainicial: moment().startOf('month').format('YYYY-MM-DD'),
        datafinal: moment().endOf('month').format('YYYY-MM-DD')
    };
    public empresas: Array<Empresa>;
    public estabelecimentos: Array<Estabelecimento>;
    public opcoesEmpresas: Array<{ id: string, nome: string }> = [];
    public opcoesEstabelecimentos: Array<{ id: string, nome: string }> = [];
    public filters: IRotatividadeIndexFilter = {
        tipo: '',
        agrupamento: 'nenhum',
        exibirtotais: true
    };
    public listagemPermissao: Array<string> = ['relatorios_rotatividade'];
    public permissaoGrupoEmpresarial = false;

    public orderbyparams: any = {
        admissoes: 'nome',
        rescisoes: 'nome'
    };

    constructor(
        public $rootScope: angular.IRootScopeService & {
            nsjGlobals: any,
            temPermissao: (arg: string) => boolean,
            temPermissaoGrupoEmpresarial: (permissoes: string[], grupo: string) => boolean,
            session: any
        },
        public $state: angular.ui.IStateService,
        public $stateParams: angular.ui.IStateParamsService,
        public NewToaster: { pop: Function },
        public RotatividadeService: RotatividadeService,
        public ListagemService: ListagemService,
        public RelatoriosService: any,
    ) {
        if (!$rootScope.temPermissao('relatorios_rotatividade')) {
            $state.go('dp', $stateParams);
        }

        this.filters = {
            ...this.filters,
            empresa: $rootScope.session.entities.empresa.empresa,
            estabelecimento: $rootScope.session.entities.estabelecimento.estabelecimento
        };

        this.permissaoGrupoEmpresarial = $rootScope.temPermissaoGrupoEmpresarial(
            this.listagemPermissao,
            $rootScope.nsjGlobals.a.grupoempresarial
        );

        this.montaListagemVisibilidade();
    }

    carregarRelatorio() {
        if (!this.validarData()) {
            return;
        }
        this.carregando = true;
        this.RotatividadeService.carregarAgrupado(this.setConstructor())
            .then(resultado => {
                this.resultado = resultado;
                this.carregando = false;
            })
            .catch(erro => {
                // requisição cancelada por uma troca de filtro mais recente: a nova assume
                if (erro && erro.xhrStatus === 'abort') {
                    return;
                }
                this.carregando = false;
                this.NewToaster.pop({
                    type: 'error',
                    title: (erro && erro.data && erro.data.message) || 'Não foi possível carregar o relatório de rotatividade.'
                });
            });
    }

    montaListagemVisibilidade() {
        this.empresas = this.ListagemService.montaEmpresas(this.$rootScope.nsjGlobals.a.grupoempresarial, this.listagemPermissao);
        this.opcoesEmpresas = [
            ...(this.permissaoGrupoEmpresarial ? [{ id: '', nome: 'Todas' }] : []),
            ...this.ordenarPorNome(this.empresas.map((empresa: any) => ({ id: empresa.id, nome: empresa.razaosocial })))
        ];
        this.montaEstabelecimentos();
    }

    montaEstabelecimentos() {
        this.estabelecimentos = this.filters.empresa
            ? this.ListagemService.montaEstabelecimentos(this.filters.empresa, this.empresas, this.listagemPermissao)
            : [];
        this.opcoesEstabelecimentos = [
            { id: '', nome: 'Todos' },
            ...this.ordenarPorNome(this.estabelecimentos.map((estabelecimento: any) => ({ id: estabelecimento.id, nome: estabelecimento.nomefantasia })))
        ];
    }

    /**
     * Trocar a empresa leva estabelecimento, lotação e cargo de volta a "Todos":
     * nenhum deles pertence necessariamente à nova empresa.
     */
    mudaEmpresa() {
        this.montaEstabelecimentos();
        this.filters.estabelecimento = '';
        this.filters.lotacao = null;
        this.filters.cargo = null;
        this.$rootScope.$broadcast('lotacao_refresh', this.constructorEmpresaEstabelecimento());
        this.$rootScope.$broadcast('cargo_refresh', this.constructorEmpresaEstabelecimento());
        this.carregarRelatorio();
    }

    /**
     * Trocar o estabelecimento leva só a lotação de volta a "Todos": o cargo é
     * da empresa, e continua valendo.
     */
    mudaEstabelecimento() {
        this.filters.lotacao = null;
        this.$rootScope.$broadcast('lotacao_refresh', this.constructorEmpresaEstabelecimento());
        this.carregarRelatorio();
    }

    /**
     * "Todas"/"Todos" vão como string vazia, e não omitidos: o nsjRouting acrescenta
     * o estabelecimento da sessão a toda requisição e só deixa de acrescentar quando
     * o parâmetro vem vazio, que ele mesmo retira da URL (core/routing/nsj-rotas.ts).
     * Omitido, "Todos" viraria o estabelecimento da sessão no relatório, na impressão
     * e nas listas de Lotação e Cargo.
     */
    constructorEmpresaEstabelecimento() {
        return {
            empresa: this.filters.empresa || '',
            estabelecimento: this.filters.estabelecimento || ''
        };
    }

    /**
     * Tipo, lotação e cargo em "Todos" não vão na requisição: filtrar por valor
     * vazio não significa "todos" para o backend.
     */
    setConstructor() {
        let dataConstructor = {};
        if (this.modo_selecao === 'competencia') {
            if (this.competencia.mes === '00') {
                const data = moment({ year: this.competencia.ano });
                dataConstructor = {
                    datainicial: data.startOf('year').format('YYYY-MM-DD'),
                    datafinal: data.endOf('year').format('YYYY-MM-DD')
                };
            } else {
                const data = moment({ year: this.competencia.ano, month: (this.competencia.mes - 1) });
                dataConstructor = {
                    datainicial: data.startOf('month').format('YYYY-MM-DD'),
                    datafinal: data.endOf('month').format('YYYY-MM-DD')
                };
            }
        } else {
            dataConstructor = {
                datainicial: angular.copy(this.range.datainicial),
                datafinal: angular.copy(this.range.datafinal)
            };
        }
        return {
            ...this.constructorEmpresaEstabelecimento(),
            ...(this.filters.tipo !== '' && { tipo: this.filters.tipo }),
            ...(this.filters.cargo && { cargo: this.filters.cargo.cargo }),
            ...(this.filters.lotacao && { lotacao: this.filters.lotacao.lotacao }),
            agrupamento: this.filters.agrupamento,
            order: this.getOrderParams(),
            ...dataConstructor
        };
    }

    getOrderParams() {
        return {
            [this.getOrderKey('admissoes')]: 'ASC',
            [this.getOrderKey('rescisoes')]: 'ASC'
        };
    }

    getOrderKey(aba: 'admissoes' | 'rescisoes') {
        const campoOrdenacao = this.orderbyparams[aba];

        if (aba === 'admissoes') {
            return campoOrdenacao === 'dataadmissao'
                ? 'dataadmissaoAdmissao'
                : 'nomeAdmissao';
        }

        return campoOrdenacao === 'datarescisao'
            ? 'datarescisaoRescisao'
            : 'nomeRescisao';
    }

    abrirRelatorio() {
        this.carregandoRelatorio = true;
        const construtor = {
            ...this.setConstructor(),
            exibirtotais: this.filters.exibirtotais ? 1 : 0
        };
        const callback = () => {
            this.carregandoRelatorio = false;
        };
        this.RelatoriosService.abrirRelatorioFop('relatoriosliberados_rotatividadefop', construtor, callback, false);
    }

    validarData() {
        if (moment(this.range.datainicial) > moment(this.range.datafinal)) {
            return false;
        }
        if (!this.range.datainicial) {
            return false;
        }
        if (!this.range.datafinal) {
            return false;
        }
        return true;
    }

    ordenarLista(aba: 'admissoes' | 'rescisoes', campoOrdenacao: 'nome' | 'dataadmissao' | 'datarescisao') {
        this.orderbyparams[aba] = campoOrdenacao;
        this.carregarRelatorio();
    }

    private ordenarPorNome(opcoes: Array<{ id: string, nome: string }>) {
        return opcoes.sort((a, b) => (a.nome || '').localeCompare(b.nome || '', 'pt-BR'));
    }
}
